import { useState, useMemo, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Info, RotateCcw } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { getMenuInfoForPath, findAllowedMenuLabel } from '@/config/menuInfo';
import { useAuth } from '@/contexts/AuthContext';
import { resetMenuTour } from './MenuTour';

/**
 * "What is this page?" banner rendered at the top of every MFE page.
 *
 * Content is looked up in the shell-level menuInfo registry by the current
 * pathname (longest-prefix match). Renders nothing when there is no entry —
 * adding one is a single-line addition in `apps/shell/src/config/menuInfo.ts`.
 *
 * Collapsed by default so it does not eat vertical space on repeat visits;
 * per-path open state is remembered in localStorage.
 */
export function PageInfoBanner(): JSX.Element | null {
  const { pathname } = useLocation();
  const { getAllowedMenus } = useAuth();
  const info = useMemo(() => {
    const base = getMenuInfoForPath(pathname);
    if (!base) return base;
    // Title = the menu name configured in super-admin (falls back to the static one).
    const dbLabel = findAllowedMenuLabel(getAllowedMenus() as Array<{ label?: string; path?: string; children?: unknown[] }>, pathname);
    return dbLabel ? { ...base, title: dbLabel } : base;
  }, [pathname, getAllowedMenus]);
  const storageKey = info ? `esg.pageInfo.open.${info.key}` : '';
  const [open, setOpen] = useState<boolean>(() => {
    if (!storageKey) return false;
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  if (!info) return null;

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        if (next) localStorage.setItem(storageKey, '1');
        else localStorage.removeItem(storageKey);
      } catch {
        /* silent */
      }
      return next;
    });
  };

  const hasTour = (info.tourSteps?.length ?? 0) > 0;
  const handleReplayTour = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetMenuTour(pathname);
  };

  // Compact variant: a small "Replay tour ›" control pinned to the right; the
  // chevron opens the same About content in a small box under it (right side).
  if (info.compact) {
    return (
      // Zero-height wrapper: the control floats at the top-right of the page and
      // takes no vertical space, so the page content starts right under the top bar.
      <div className="relative mx-6 h-0 overflow-visible">
        <div className="absolute right-0 top-2 z-20 inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
          {hasTour ? (
            <button
              type="button"
              onClick={handleReplayTour}
              title="Replay the onboarding tour for this page"
              aria-label={`Replay tour for ${info.title}`}
              className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100/70 hover:text-emerald-900 dark:text-emerald-400 dark:hover:bg-emerald-900/30 dark:hover:text-emerald-200"
            >
              <RotateCcw className="h-3 w-3" aria-hidden="true" />
              <span>Replay tour</span>
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 px-1 text-[10px] font-medium text-slate-600 dark:text-slate-300">
              <Info className="h-3 w-3 text-emerald-600" aria-hidden="true" /> About
            </span>
          )}
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-label={open ? `Hide about ${info.title}` : `About ${info.title}`}
            title={`About this page — ${info.title}`}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </div>
        {open && (
          <div className="absolute right-0 top-9 z-30 w-[230px] max-w-[calc(100vw-3rem)] rounded-md border border-slate-200 border-l-2 border-l-emerald-500 bg-white p-2 text-[10px] leading-snug text-slate-600 shadow-lg dark:border-slate-700 dark:border-l-emerald-500 dark:bg-slate-800 dark:text-slate-300">
            <div className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold text-slate-700 dark:text-slate-200">
              <Info className="h-3 w-3 text-emerald-600" /> About — {info.title}
            </div>
            <div className="space-y-1.5">
              {info.description && <Section label="What is this workspace?" body={info.description} small />}
              {info.workflow && <Section label="Workflow" body={info.workflow} small />}
              {info.purpose && <Section label="Why this page exists" body={info.purpose} small />}
              {hasTour && (
                <button
                  type="button"
                  onClick={() => resetMenuTour(pathname)}
                  className="inline-flex items-center gap-1 pt-0.5 text-[10px] font-medium text-blue-700 hover:text-blue-900 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <RotateCcw className="h-2.5 w-2.5" aria-hidden="true" />
                  Replay tour ({info.tourSteps!.length} step{info.tourSteps!.length === 1 ? '' : 's'})
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-6 mt-4 rounded-md border border-slate-200 border-l-4 border-l-emerald-500 bg-slate-50 dark:bg-slate-800/60 dark:border-slate-700 dark:border-l-emerald-500">
      <div className="w-full flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="flex items-center gap-2 flex-1 text-left"
        >
          <Info className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 flex-1">
            About this page — {info.title}
          </span>
        </button>
        {hasTour && (
          <button
            type="button"
            onClick={handleReplayTour}
            title="Replay the onboarding tour for this page"
            aria-label={`Replay tour for ${info.title}`}
            className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-200 px-1.5 py-0.5 rounded hover:bg-emerald-100/70 dark:hover:bg-emerald-900/30"
          >
            <RotateCcw className="w-3 h-3" aria-hidden="true" />
            <span className="hidden sm:inline">Replay tour</span>
          </button>
        )}
        <button
          type="button"
          onClick={toggle}
          aria-label={open ? 'Collapse' : 'Expand'}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        >
          {open ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
      {open && (
        <div className="px-3 pb-3 text-xs text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-200 dark:border-slate-700">
          <div className="pt-2 space-y-2">
            {info.description && <Section label="What is this workspace?" body={info.description} />}
            {info.workflow && <Section label="Workflow" body={info.workflow} />}
            {info.purpose && <Section label="Why this page exists" body={info.purpose} />}
            {info.tourSteps && info.tourSteps.length > 0 && (
              <div className="pt-1">
                <button
                  type="button"
                  onClick={() => resetMenuTour(pathname)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium text-blue-700 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:underline"
                  title="Replay the onboarding tour for this page"
                >
                  <RotateCcw className="w-3 h-3" aria-hidden="true" />
                  Replay tour ({info.tourSteps.length} step{info.tourSteps.length === 1 ? '' : 's'})
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ label, body, small = false }: { label: string; body: ReactNode; small?: boolean }): JSX.Element {
  return (
    <div>
      <div className={`${small ? 'text-[9px]' : 'text-[10px]'} uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold mb-0.5`}>
        {label}
      </div>
      <div>{body}</div>
    </div>
  );
}

export default PageInfoBanner;
